/**
 * Whole calendar days from today to the given date, in the browser's time
 * zone (negative once it has passed). Time of day is ignored, so an event
 * tonight is 0 — "Today" — however late it starts.
 */
export function daysUntil(date: Date | string, now: Date = new Date()): number {
  const d = new Date(date);
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/** "Today", "Tomorrow", "in 12 days", "Yesterday", "5 days ago". */
export function countdownLabel(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days < 0) return `${Math.abs(days)} days ago`;
  return `in ${days} days`;
}

export type CountdownTone = 'past' | 'soon' | 'month' | 'later';

/** How urgent an event is: this week (soon), this month, later, or over. */
export function countdownTone(days: number): CountdownTone {
  if (days < 0) return 'past';
  if (days <= 7) return 'soon';
  if (days <= 30) return 'month';
  return 'later';
}

/**
 * True for an all-day event: stored starting at local midnight and either
 * with no end or ending at 11:59 PM of a day. A midnight start with any other
 * end is an event that really does start at 12:00 AM.
 */
export function isAllDayEvent(startAt: Date | string, endAt?: Date | string | null): boolean {
  const s = new Date(startAt);
  if (s.getHours() !== 0 || s.getMinutes() !== 0) return false;
  if (!endAt) return true;
  const e = new Date(endAt);
  return e.getHours() === 23 && e.getMinutes() === 59;
}

/** "Sat, Oct 17, 2026 · 6:00 PM" — the event header's date; the day alone when all day. */
export function formatEventDateTime(date: Date | string, endAt?: Date | string | null): string {
  const d = new Date(date);
  const day = d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  if (isAllDayEvent(d, endAt)) return day;
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

/** "Oct 17" in the current year, "Oct 17, 2027" otherwise. */
export function formatShortDate(date: Date | string, now: Date = new Date()): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() && { year: 'numeric' }),
  });
}

export type DateRangeKey = 'week' | 'month' | 'upcoming' | 'past' | 'all';

export const DATE_RANGE_OPTIONS: { id: DateRangeKey; label: string }[] = [
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
  { id: 'all', label: 'All' },
];

/**
 * The instants the This month range spans: local midnight today to local
 * midnight 31 days on (exclusive) — today and the 30 days after it, as
 * `inDateRange` reads them. A count that opens this range asks for these.
 */
export function monthRangeWindow(now: Date = new Date()): { start: string; end: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 31);
  return { start: start.toISOString(), end: end.toISOString() };
}

/**
 * Whether an event starting at `date` falls in the quick range. "This week"
 * and "This month" are the next 7 / 30 days from today (an event today
 * counts); "Upcoming" is today onward; "Past" is before today.
 */
export function inDateRange(
  date: Date | string | null | undefined,
  range: DateRangeKey,
  now: Date = new Date(),
): boolean {
  if (range === 'all') return true;
  if (!date) return false;
  const days = daysUntil(date, now);
  switch (range) {
    case 'week':
      return days >= 0 && days <= 7;
    case 'month':
      return days >= 0 && days <= 30;
    case 'upcoming':
      return days >= 0;
    case 'past':
      return days < 0;
  }
}
